import { Fix64 } from "../maths/fix64";

/**
 * Gold Research (M35, docs/RESEARCH.md; balance/src/research.json): a five-node permanent tree per tower type and one
 * for the Fortress, bought once with gold. gen.py expands the tree templates into one node per tower ('<tower>.<key>')
 * with its gate, price and tower-level worth; this is the parsed bundle section. The rules that read it are
 * meta/research.ts. An older bundle has no section: `emptyResearch` has no nodes, so nothing can be researched.
 */

/** The GearBonus field a node adds to (hp and troops only on the Fortress's tree). */
export type ResearchStat = "damage" | "range" | "points" | "hp" | "troops";
export const RESEARCH_STATS: readonly ResearchStat[] = ["damage", "range", "points", "hp", "troops"];

export interface ResearchNodeDef {
  /** '<tower id>.<key>', e.g. "archer.edge". The save stores these. */
  readonly id: string;
  /** The tower archetype index the node belongs to (the base's own index for the Fortress tree). */
  readonly towerIndex: number;
  readonly towerId: string;
  readonly key: string;
  readonly name: string;
  readonly stat: ResearchStat;
  /** Whole percents (4 → +4 %). */
  readonly percent: number;
  /** The same as a fraction (0.04), what GearBonus adds. */
  readonly value: Fix64;
  /** Index into the tiers (0 = the root's). */
  readonly tier: number;
  /** The persistent level the tree's tower (or the Fortress) needs. */
  readonly gate: number;
  /** Its price in gold. */
  readonly gold: number;
  /** Node ids that must all be owned first (same tree). */
  readonly requires: readonly string[];
  /** The tower levels it counts as on the readiness towers meter (presentation arithmetic; never a sim input). */
  readonly worth: number;
}

export interface ResearchData {
  /** expectedResearch: a node is expected once the campaign level is this far past its gate. */
  readonly expectLagLevels: number;
  readonly nodes: readonly ResearchNodeDef[];
}

export function emptyResearch(): ResearchData { return { expectLagLevels: 0, nodes: [] }; }

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Json = any;

const PERCENT = 100;

function whole(v: Json, where: string, min = 0): number {
  if (typeof v === "number" && Number.isInteger(v) && v >= min) return v;
  throw new TypeError(`${where}: expected a whole number >= ${min}, got ${JSON.stringify(v)}`);
}

/** Reads the bundle's `research` section (gen.py build_research has checked it; this re-checks shape and throws otherwise). */
export function parseResearch(root: Json, towerIds: readonly string[]): ResearchData {
  const s = root?.research;
  if (s === undefined) return emptyResearch();
  const nodes = (s.nodes as Json[]).map((n, i): ResearchNodeDef => {
    const where = `research node ${i} (${String(n?.id)})`;
    const towerIndex = towerIds.indexOf(String(n.tower));
    if (towerIndex < 0) throw new Error(`${where}: unknown tower ${n.tower}`);
    if (!RESEARCH_STATS.includes(n.stat)) throw new Error(`${where}: unknown stat ${n.stat}`);
    const percent = whole(n.percent, where, 1);
    const worth = Number(n.worth);
    if (!Number.isFinite(worth) || worth < 0) throw new Error(`${where}: worth`);
    return {
      id: String(n.id), towerIndex, towerId: String(n.tower), key: String(n.key), name: String(n.name), stat: n.stat as ResearchStat,
      percent, value: Fix64.fromFraction(percent, PERCENT), tier: whole(n.tier, where), gate: whole(n.gate, where, 1), gold: whole(n.gold, where, 1),
      requires: (n.requires as Json[]).map(String), worth,
    };
  });
  const ids = new Set(nodes.map((n) => n.id));
  if (ids.size !== nodes.length) throw new Error("research: node ids must be unique");
  for (const n of nodes) for (const r of n.requires) if (!ids.has(r)) throw new Error(`research ${n.id}: requires unknown ${r}`);
  return { expectLagLevels: whole(s.expectLagLevels, "research.expectLagLevels"), nodes };
}
